import { PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RsvpQuestionFormValues } from '@bridebook/web/lib/rsvp/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { rsvpAnswerTypes } from '@bridebook/web/lib/rsvp/constants/events';
import styles from '@bridebook/web/components/wedding-website/wedding-details/sections/rsvp/add-edit-question-form.style';
import { useFela } from 'react-fela';
import { useRsvpFormSubmit } from '@bridebook/web/lib/rsvp/hooks/use-rsvp-form-submit';
import { useUnifiedEvents } from '@bridebook/web/lib/events/use-unified-events';
import { useWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-wedding-rsvp';
import { useUpdateWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-update-wedding-rsvp';
import { useEventOptionsWithHandlers } from '@bridebook/web/lib/rsvp/hooks/use-event-options-with-handlers';
import { TFunction } from 'next-i18next';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

const getFormSchema = (t: TFunction) =>
  z
    .object({
      question: z.string().min(1, t('weddingWebsite:rsvp.event.question.required')),
      type: z.enum(rsvpAnswerTypes),
      events: z.array(z.string()).min(1, t('weddingWebsite:rsvp.event.events.required')),
      options: z.array(z.string().min(1)).optional(),
    })
    .refine(
      (data) => {
        if (data.type === 'list') {
          return data.options && data.options.length > 0;
        }
        return true;
      },
      {
        message: t('weddingWebsite:rsvp.event.list.options.required'),
        path: ['options'],
      },
    );

export type FormValues = z.infer<ReturnType<typeof getFormSchema>>;

interface IProps extends PropsWithChildren {
  questionId?: string;
  onSuccess: () => void;
}

export const AddEditQuestionForm = ({ questionId, onSuccess, children }: IProps) => {
  const isNewEntry = !questionId || questionId === 'add';
  const { t } = useTranslation('weddingWebsite');
  const { css } = useFela();
  const { unifiedEvents, isLoading: isUnifiedEventsLoading } = useUnifiedEvents();
  const { data: weddingRsvp, isLoading: isWeddingRsvpQueryLoading } = useWeddingRsvp();
  const { mutateAsync: updateWeddingRsvpAsync } = useUpdateWeddingRsvp();

  const formSchema = useMemo(() => getFormSchema(t), [t]);

  const form = useForm<RsvpQuestionFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: '',
      type: 'string',
      events: [],
      options: [],
    },
  });

  const { handleSubmit, setValue, getValues, reset } = form;
  const isDataLoaded =
    weddingRsvp && !isWeddingRsvpQueryLoading && !isNewEntry && typeof questionId === 'string';

  useEffect(() => {
    if (!isDataLoaded || !weddingRsvp) return;
    const entry = weddingRsvp.questions.find((q) => q.id === questionId);

    if (!entry) return;

    reset({
      question: entry.question,
      type: entry.type,
      events: entry.events,
      options: entry.type === 'list' ? (entry.options ?? []) : [],
    });
  }, [questionId, isDataLoaded, reset, weddingRsvp]);

  const { eventOptions } = useEventOptionsWithHandlers({
    isLoading: isUnifiedEventsLoading,
    unifiedEvents,
    getValues,
    setValue,
  });

  const onSubmit = useRsvpFormSubmit({
    id: questionId ?? 'add',
    isNewEntry,
    weddingRsvp,
    updateWeddingRsvpAsync,
    eventsCount: eventOptions.length,
    unifiedEvents,
    onSuccess,
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={css(styles.form)}>
        {children}
      </form>
    </FormProvider>
  );
};
